import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';

export interface AdminAccessPermissions {
  default_access: boolean;
  restricted_routes: string[];
  // Restriction par domaine
  // Si allowed_domains est vide ou absent : accès à tous les domaines
  // Si allowed_domains contient des domaines : accès UNIQUEMENT à ces domaines
  allowed_domains?: string[];
}

export interface UserPermissions {
  contests?: string[];
  features?: string[];
  contest_permissions?: Record<string, any>;
  admin_access?: AdminAccessPermissions;
}

interface AdminPermissionsState {
  permissions: UserPermissions | null;
  isLoading: boolean;
  isSuperAdmin: boolean;
  restrictedRoutes: string[];
  hasAccessToRoute: (path: string) => boolean;
  getRestrictedRoutes: () => string[];
  refreshPermissions: () => Promise<void>;
}

// Liste des modules admin disponibles pour la restriction
export const ADMIN_MODULES = [
  { id: 'dashboard', name: 'Tableau de bord', route: '/admin/dashboard', icon: 'LayoutDashboard' },
  { id: 'contests', name: 'Concours', route: '/admin/contests', icon: 'Trophy', hasSubroutes: true },
  { id: 'contestants', name: 'Candidats', route: '/admin/contestants', icon: 'Users', hasSubroutes: true },
  { id: 'transactions', name: 'Transactions', route: '/admin/transactions', icon: 'CreditCard' },
  { id: 'clients', name: 'Clients', route: '/admin/clients', icon: 'Users', hasSubroutes: true },
  { id: 'awards', name: 'Awards', route: '/admin/awards', icon: 'Trophy', hasSubroutes: true },
  { id: 'crowdfunding', name: 'Crowdfunding', route: '/admin/crowdfunding', icon: 'HeartHandshake', hasSubroutes: true },
  { id: 'messages', name: 'Messages', route: '/admin/messages', icon: 'MessageSquare' },
  { id: 'emails', name: 'Emails', route: '/admin/emails', icon: 'Mail' },
  { id: 'blog', name: 'Blog', route: '/admin/blog', icon: 'FileText', hasSubroutes: true },
  { id: 'partners', name: 'Partenaires', route: '/admin/partners', icon: 'Building', hasSubroutes: true },
  { id: 'portfolio', name: 'Portfolio', route: '/admin/portfolio', icon: 'Image', hasSubroutes: true },
  { id: 'boutique', name: 'Boutique', route: '/admin/boutique', icon: 'ShoppingBag', hasSubroutes: true },
  { id: 'elearning', name: 'E-Learning', route: '/admin/elearning', icon: 'BookOpen', hasSubroutes: true },
  { id: 'psy', name: 'Santé Psy', route: '/admin/psy', icon: 'Stethoscope', hasSubroutes: true },
  { id: 'coupons', name: 'Coupons', route: '/admin/coupons', icon: 'Ticket', hasSubroutes: true },
  { id: 'dating', name: 'Dating', route: '/admin/dating', icon: 'Heart', hasSubroutes: true },
  { id: 'events', name: 'Événements', route: '/admin/events', icon: 'Calendar', hasSubroutes: true },
  { id: 'configuration', name: 'Configuration', route: '/admin/configuration', icon: 'Settings' },
  { id: 'mediatheque', name: 'Médiathèque', route: '/admin/mediatheque', icon: 'Image' },
  { id: 'tenants', name: 'Tenants', route: '/admin/tenants', icon: 'Building' },
  { id: 'users', name: 'Utilisateurs', route: '/admin/users', icon: 'Users', hasSubroutes: true },
];

// Sous-routes détaillées pour chaque module
export const ADMIN_SUBROUTES: Record<string, { name: string; route: string }[]> = {
  contests: [
    { name: 'Liste des concours', route: '/admin/contests' },
    { name: 'Éditer un concours', route: '/admin/contests/edit' },
    { name: 'Concours en attente', route: '/admin/contests/pending' },
  ],
  contestants: [
    { name: 'Liste des candidats', route: '/admin/contestants' },
    { name: 'Candidats en attente', route: '/admin/contestants/pending' },
  ],
  clients: [
    { name: 'Liste des clients', route: '/admin/clients' },
    { name: 'Finances client', route: '/admin/clients/finances' },
  ],
  awards: [
    { name: 'Liste des awards', route: '/admin/awards' },
    { name: 'Créer un award', route: '/admin/awards/new' },
    { name: 'Éditer un award', route: '/admin/awards/edit' },
  ],
  crowdfunding: [
    { name: 'Campagnes', route: '/admin/crowdfunding' },
    { name: 'Créer une campagne', route: '/admin/crowdfunding/create' },
    { name: 'Statistiques', route: '/admin/crowdfunding/stats' },
  ],
  blog: [
    { name: 'Articles', route: '/admin/blog' },
    { name: 'Créer un article', route: '/admin/blog/create' },
    { name: 'Catégories', route: '/admin/blog/categories' },
    { name: 'Commentaires', route: '/admin/blog/comments' },
  ],
  partners: [
    { name: 'Liste des partenaires', route: '/admin/partners' },
    { name: 'Nouveau partenaire', route: '/admin/partners/new' },
  ],
  portfolio: [
    { name: 'Œuvres', route: '/admin/portfolio' },
    { name: 'Nouvelle œuvre', route: '/admin/portfolio/create' },
    { name: 'Catégories', route: '/admin/portfolio/categories' },
  ],
  boutique: [
    { name: 'Produits', route: '/admin/boutique' },
    { name: 'Créer un produit', route: '/admin/boutique/creer' },
    { name: 'Commandes', route: '/admin/boutique/commandes' },
    { name: 'Catégories', route: '/admin/boutique/categories' },
  ],
  elearning: [
    { name: 'Cours', route: '/admin/elearning' },
    { name: 'Créer un cours', route: '/admin/elearning/create' },
    { name: 'Inscriptions', route: '/admin/elearning/enrollments' },
    { name: 'Catégories', route: '/admin/elearning/categories' },
    { name: 'Instructeurs', route: '/admin/elearning/instructors' },
    { name: 'Étudiants', route: '/admin/elearning/students' },
    { name: 'Avis', route: '/admin/elearning/reviews' },
  ],
  psy: [
    { name: 'Praticiens', route: '/admin/psy/praticiens' },
    { name: 'Patients', route: '/admin/psy/patients' },
    { name: 'Rendez-vous', route: '/admin/psy/appointments' },
    { name: 'Triage', route: '/admin/psy/triage' },
  ],
  coupons: [
    { name: 'Liste des coupons', route: '/admin/coupons' },
    { name: 'Nouveau coupon', route: '/admin/coupons/new' },
  ],
  dating: [
    { name: 'Dashboard', route: '/admin/dating' },
    { name: 'Profils', route: '/admin/dating/profiles' },
    { name: 'Signalements', route: '/admin/dating/reports' },
    { name: 'Plans', route: '/admin/dating/plans' },
    { name: 'Commandes', route: '/admin/dating/orders' },
  ],
  events: [
    { name: 'Liste des événements', route: '/admin/events' },
    { name: 'Créer un événement', route: '/admin/events/create' },
  ],
  users: [
    { name: 'Permissions admin', route: '/admin/users/permissions' },
  ],
};

export function useAdminPermissions(): AdminPermissionsState {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Charger les permissions depuis la base de données
  const loadPermissions = useCallback(async () => {
    if (!user?.id) {
      setPermissions(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('role, permissions')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Erreur lors du chargement des permissions:', error);
        setPermissions(null);
      } else {
        setPermissions(data?.permissions || null);
        setIsSuperAdmin(data?.role === 'super_admin');
      }
    } catch (err) {
      console.error('Exception lors du chargement des permissions:', err);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Vérifier si l'utilisateur a accès à une route
  const hasAccessToRoute = useCallback((path: string): boolean => {
    // Super admin a toujours accès
    if (isSuperAdmin) {
      return true;
    }

    // Si pas de permissions définies, accès par défaut
    if (!permissions?.admin_access) {
      return true;
    }

    const { restricted_routes } = permissions.admin_access;

    // Si pas de routes restreintes, accès autorisé
    if (!restricted_routes || restricted_routes.length === 0) {
      return true;
    }

    // Normaliser le path (enlever trailing slash)
    const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;

    // Vérifier chaque pattern de restriction
    for (const pattern of restricted_routes) {
      // Pattern avec wildcard (ex: /admin/portfolio/*)
      // Bloque la route de base ET toutes les sous-routes
      if (pattern.endsWith('/*')) {
        const baseRoute = pattern.slice(0, -2); // Enlever /*
        // Bloquer si c'est exactement la route de base OU une sous-route
        if (normalizedPath === baseRoute || normalizedPath.startsWith(baseRoute + '/')) {
          return false;
        }
      }
      // Pattern exact - bloque uniquement cette route exacte
      else if (normalizedPath === pattern) {
        return false;
      }
    }

    return true;
  }, [permissions, isSuperAdmin]);

  // Récupérer la liste des routes restreintes
  const getRestrictedRoutes = useCallback((): string[] => {
    if (isSuperAdmin) {
      return [];
    }
    return permissions?.admin_access?.restricted_routes || [];
  }, [permissions, isSuperAdmin]);

  // Rafraîchir les permissions
  const refreshPermissions = useCallback(async () => {
    setIsLoading(true);
    await loadPermissions();
  }, [loadPermissions]);

  // Exposer les routes restreintes directement pour éviter les problèmes de mémoisation
  const restrictedRoutes = permissions?.admin_access?.restricted_routes || [];

  return {
    permissions,
    isLoading,
    isSuperAdmin,
    restrictedRoutes,
    hasAccessToRoute,
    getRestrictedRoutes,
    refreshPermissions,
  };
}

// Fonction utilitaire pour mettre à jour les restrictions d'un utilisateur
export async function updateUserAdminRestrictions(
  userId: string,
  restrictedRoutes: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Récupérer les permissions actuelles
    const { data: userData, error: fetchError } = await supabase
      .from('users')
      .select('permissions')
      .eq('id', userId)
      .single();

    if (fetchError) {
      return { success: false, error: fetchError.message };
    }

    // Construire les nouvelles permissions
    const currentPermissions = userData?.permissions || {};
    const newPermissions = {
      ...currentPermissions,
      admin_access: {
        default_access: true,
        restricted_routes: restrictedRoutes,
      },
    };

    // Mettre à jour
    const { error: updateError } = await supabase
      .from('users')
      .update({ permissions: newPermissions })
      .eq('id', userId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

// Fonction pour récupérer les permissions d'un utilisateur spécifique
export async function getUserAdminRestrictions(
  userId: string
): Promise<{ restrictedRoutes: string[]; allowedDomains: string[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('permissions')
      .eq('id', userId)
      .single();

    if (error) {
      return { restrictedRoutes: [], allowedDomains: [], error: error.message };
    }

    return {
      restrictedRoutes: data?.permissions?.admin_access?.restricted_routes || [],
      allowedDomains: data?.permissions?.admin_access?.allowed_domains || [],
    };
  } catch (err: any) {
    return { restrictedRoutes: [], allowedDomains: [], error: err.message };
  }
}

// Fonction utilitaire pour mettre à jour les restrictions d'un utilisateur (routes + domaines)
export async function updateUserAdminPermissions(
  userId: string,
  restrictedRoutes: string[],
  allowedDomains: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Récupérer les permissions actuelles
    const { data: userData, error: fetchError } = await supabase
      .from('users')
      .select('permissions')
      .eq('id', userId)
      .single();

    if (fetchError) {
      return { success: false, error: fetchError.message };
    }

    // Construire les nouvelles permissions
    const currentPermissions = userData?.permissions || {};
    const newPermissions = {
      ...currentPermissions,
      admin_access: {
        default_access: true,
        restricted_routes: restrictedRoutes,
        allowed_domains: allowedDomains,
      },
    };

    // Mettre à jour
    const { error: updateError } = await supabase
      .from('users')
      .update({ permissions: newPermissions })
      .eq('id', userId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}
